import React, { useEffect, useState } from "react";
import axios from "axios";
import { Swords, Trophy, Skull, Loader2, Flame } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RivalCard
 * --------
 * Shows the user's weekly rival, live score, and last result.
 * `variant="compact"`  — for the Dashboard (small).
 * `variant="full"`     — for the Teams page (large, includes history teaser).
 */
const RivalCard = ({ headers, variant = "compact" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const h = headers || { Authorization: `Bearer ${localStorage.getItem("token")}` };
        const res = await axios.get(`${API}/rivals/current`, { headers: h });
        if (mounted) setData(res.data);
      } catch { /* silent */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [headers]);

  if (loading) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-5 flex items-center gap-3" data-testid="rival-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-white/40" />
        <span className="text-xs font-mono text-white/40">SCOUTING RIVAL...</span>
      </div>
    );
  }

  if (!data?.has_rival) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-5" data-testid="rival-card-empty">
        <div className="flex items-center gap-3 mb-2">
          <Swords className="w-5 h-5 text-white/30" />
          <p className="text-sm font-heading uppercase tracking-wider text-white/60">No Rival Yet</p>
        </div>
        <p className="text-xs text-white/40 font-mono">{data?.reason || "Join a team to unlock weekly Rivals."}</p>
      </div>
    );
  }

  const { rival, my_points, rival_points, delta, last_result } = data;
  const winning = delta > 0;
  const losing = delta < 0;
  const tied = delta === 0;

  const accent = rival.team_color || "#FF4500";

  return (
    <div
      className="border border-white/[0.06] overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${accent}15 0%, #0A0A0A 70%)` }}
      data-testid="rival-card"
    >
      <div className="px-5 py-2 flex items-center justify-between" style={{ background: "rgba(0,0,0,0.4)" }}>
        <div className="flex items-center gap-2">
          <Swords className="w-3.5 h-3.5" style={{ color: accent }} />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>
            WEEKLY RIVAL
          </p>
        </div>
        {tied ? (
          <span className="text-[9px] font-mono uppercase tracking-wider text-white/40">DEAD HEAT</span>
        ) : winning ? (
          <span className="text-[9px] font-mono uppercase tracking-wider text-[#CCFF00] flex items-center gap-1">
            <Flame className="w-3 h-3" /> AHEAD
          </span>
        ) : (
          <span className="text-[9px] font-mono uppercase tracking-wider text-red-400 flex items-center gap-1">
            <Skull className="w-3 h-3" /> BEHIND
          </span>
        )}
      </div>

      <div className="p-5">
        {/* Head-to-head */}
        <div className="grid grid-cols-3 items-center gap-3">
          <div className="text-center" data-testid="rival-card-me">
            <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-1">YOU</p>
            <p className="text-3xl md:text-4xl font-heading text-white">{my_points}</p>
            <p className="text-[9px] font-mono text-white/30 mt-0.5">PTS THIS WEEK</p>
          </div>

          <div className="text-center">
            <p className="text-2xl font-heading" style={{ color: winning ? "#CCFF00" : losing ? "#EF4444" : "#FFFFFF" }}>
              {delta > 0 ? `+${delta}` : delta}
            </p>
            <p className="text-[9px] font-mono uppercase text-white/30">DELTA</p>
          </div>

          <div className="text-center" data-testid="rival-card-rival">
            <p className="text-[9px] font-mono uppercase tracking-wider mb-1 truncate" style={{ color: accent }}>
              {rival.name?.toUpperCase().substring(0, 14) || "RIVAL"}
            </p>
            <p className="text-3xl md:text-4xl font-heading text-white">{rival_points}</p>
            <p className="text-[9px] font-mono text-white/30 mt-0.5 truncate">{rival.team_name || "—"}</p>
          </div>
        </div>

        {/* Last result strip */}
        {last_result && last_result.winner_id && (
          <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center gap-2 text-xs font-mono">
            <Trophy className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-white/40">
              LAST WEEK:{" "}
              <span className="text-white">
                {last_result.winner_id === data.rival.user_id ? rival.name : "YOU"} won
              </span>
              <span className="text-white/30"> · {last_result.final_user_points}–{last_result.final_rival_points}</span>
            </span>
          </div>
        )}

        {variant === "full" && (
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider mt-4 leading-relaxed">
            Beat them by Sunday 23:59 UTC to win bonus points. New rival every Monday.
          </p>
        )}
      </div>
    </div>
  );
};

export default RivalCard;
